// Portfolio performance time-series calculations.
//
// Histories come from the getPriceHistory Cloud Function:
//   { [ticker]: { timestamps: [unixSeconds], closes: [number|null], currency } }
// Transactions are the imported broker rows (see portfolioMetrics.js).

import { normalizeQuote } from "./portfolioMetrics";
import { convertToBase } from "./fx";

// Per-ticker share count over time: sorted [{ ts, shares }] checkpoints.
export function buildHoldingsTimelines(transactions) {
  const byTicker = {};
  const orders = (transactions || [])
    .filter((tx) => tx.type === "ORDER" && tx.ticker && (tx.quantity || 0) > 0)
    .slice()
    .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

  for (const tx of orders) {
    if (!byTicker[tx.ticker]) byTicker[tx.ticker] = [];
    const timeline = byTicker[tx.ticker];
    const prev = timeline.length ? timeline[timeline.length - 1].shares : 0;
    const delta = tx.buySell === "BUY" ? tx.quantity : -tx.quantity;
    timeline.push({ ts: tx.timestamp || 0, shares: Math.max(0, prev + delta) });
  }
  return byTicker;
}

// Shares held at a moment in time (0 before the first transaction).
export function sharesAt(timeline, tsMs) {
  let shares = 0;
  for (const point of timeline || []) {
    if (point.ts > tsMs) break;
    shares = point.shares;
  }
  return shares;
}

// Union of all history dates (ms), sorted ascending.
export function buildDateGrid(histories) {
  const set = new Set();
  for (const h of Object.values(histories || {})) {
    for (const t of h?.timestamps || []) set.add(t * 1000);
  }
  return [...set].sort((a, b) => a - b);
}

// Returns a lookup(tsMs) giving the most recent close at or before tsMs
// (forward-fill over gaps such as exchange holidays), or null before the
// series starts.
export function makeCloseLookup(history) {
  const times = (history?.timestamps || []).map((t) => t * 1000);
  const closes = history?.closes || [];
  return (tsMs) => {
    let result = null;
    for (let i = 0; i < times.length; i++) {
      if (times[i] > tsMs) break;
      if (closes[i] !== null && closes[i] !== undefined) result = closes[i];
    }
    return result;
  };
}

// Daily portfolio market value series in the base currency (or raw instrument
// values summed when fx is null). Starts at the first day any shares are held.
// Returns [{ ts, value }].
export function buildPortfolioValueSeries(histories, transactions, fx) {
  const timelines = buildHoldingsTimelines(transactions);
  const grid = buildDateGrid(histories);
  const lookups = {};
  for (const [ticker, h] of Object.entries(histories || {})) {
    lookups[ticker] = { lookup: makeCloseLookup(h), currency: h?.currency };
  }

  const series = [];
  for (const ts of grid) {
    let value = 0;
    let anyShares = false;
    for (const [ticker, timeline] of Object.entries(timelines)) {
      const shares = sharesAt(timeline, ts);
      if (shares <= 0) continue;
      const entry = lookups[ticker];
      if (!entry) continue;
      const rawClose = entry.lookup(ts);
      if (rawClose === null) continue;
      anyShares = true;
      const { price, currency } = normalizeQuote(rawClose, entry.currency);
      const converted = fx ? convertToBase(price, currency, fx) : null;
      value += (converted ?? price) * shares;
    }
    if (anyShares) series.push({ ts, value });
  }
  return series;
}

// Net external cash flows per day (ms): buys positive, sells negative, in the
// account currency (broker totalAmount), falling back to quantity × price.
export function buildFlows(transactions, fx) {
  const flows = [];
  for (const tx of transactions || []) {
    if (tx.type !== "ORDER" || !(tx.quantity > 0)) continue;
    let amount = Number.isFinite(tx.totalAmount) ? Math.abs(tx.totalAmount) : null;
    if (amount !== null && fx && tx.accountCurrency) {
      amount = convertToBase(amount, tx.accountCurrency, fx) ?? amount;
    }
    if (amount === null) {
      const local = (tx.quantity || 0) * (tx.pricePerShare || 0);
      amount = fx && tx.instrumentCurrency
        ? (convertToBase(local, tx.instrumentCurrency, fx) ?? local)
        : local;
    }
    flows.push({ ts: tx.timestamp || 0, amount: tx.buySell === "BUY" ? amount : -amount });
  }
  return flows.sort((a, b) => a.ts - b.ts);
}

// Time-weighted return index (starts at 100): strips out the effect of
// deposits/withdrawals so the line is comparable to a benchmark.
// index_t = index_{t-1} × (V_t − F_t) / V_{t-1}, F_t = net flows in (t-1, t].
export function buildTwrIndex(valueSeries, flows) {
  if (!valueSeries || valueSeries.length === 0) return [];
  const index = [{ ts: valueSeries[0].ts, index: 100 }];
  for (let i = 1; i < valueSeries.length; i++) {
    const prev = valueSeries[i - 1];
    const curr = valueSeries[i];
    const flow = (flows || [])
      .filter((f) => f.ts > prev.ts && f.ts <= curr.ts)
      .reduce((sum, f) => sum + f.amount, 0);
    const prevIndex = index[index.length - 1].index;
    const ratio = prev.value > 0 ? (curr.value - flow) / prev.value : 1;
    index.push({ ts: curr.ts, index: prevIndex * ratio });
  }
  return index;
}

// Benchmark price series rebased to 100 at its first valid close.
export function buildBenchmarkIndex(history) {
  const times = (history?.timestamps || []).map((t) => t * 1000);
  const closes = history?.closes || [];
  let first = null;
  const series = [];
  for (let i = 0; i < times.length; i++) {
    const close = closes[i];
    if (close === null || close === undefined) continue;
    if (first === null) first = close;
    series.push({ ts: times[i], index: (close / first) * 100 });
  }
  return series;
}
